import { createBigDecimal, plus, times } from '@0xtorch/big-decimal'
import { type CryptoCurrency, type FiatCurrency, rest } from '@0xtorch/core'
import { z } from 'zod'
import type { AssetBalance } from '../../types'
import { createSha256Hash } from '../../utils/createSha256Hash'
import { getAsset } from '../../utils/getAsset'
import { binanceApiEndpoint, binanceSymbolCryptoIdMap } from '../constants'
import { marginAssetSchema } from './schema'

const responseSchema = z.object({
  userAssets: z.array(marginAssetSchema),
})

const urlPath = '/sapi/v1/margin/account'

type FetchCrossMarginAccountAssetsParameters = {
  apiKey: string
  apiSecret: string
  proxy?: string
  proxyHeaders?: Record<string, string>
  cryptoCurrencies: readonly CryptoCurrency[]
  fiatCurrencies: readonly FiatCurrency[]
}

export const fetchCrossMarginAccountAssets = async ({
  apiKey,
  apiSecret,
  proxy,
  proxyHeaders,
  cryptoCurrencies,
  fiatCurrencies,
}: FetchCrossMarginAccountAssetsParameters): Promise<
  readonly AssetBalance[]
> => {
  const timestamp = Date.now()

  const url = new URL(urlPath, binanceApiEndpoint)
  url.searchParams.append('timestamp', timestamp.toString())
  url.searchParams.append(
    'signature',
    await createSha256Hash(url.searchParams.toString(), apiSecret),
  )

  const response = await rest(
    proxy === undefined
      ? url.toString()
      : `${proxy}${encodeURIComponent(url.toString())}`,
    {
      fetchOptions: {
        method: 'GET',
        headers: {
          'X-MBX-APIKEY': apiKey,
          ...proxyHeaders,
        },
      },
      schema: responseSchema,
    },
  )

  const assetBalances: AssetBalance[] = []
  for (const assetData of response.userAssets) {
    const asset = getAsset({
      symbol: assetData.asset,
      cryptoCurrencies,
      fiatCurrencies,
      symbolCryptoIdMap: binanceSymbolCryptoIdMap,
    })
    if (asset === undefined) {
      continue
    }
    const cryptoId = asset.type === 'CryptoCurrency' ? asset.id : undefined
    const fiatId = asset.type === 'FiatCurrency' ? asset.id : undefined
    const balance = plus(assetData.free, assetData.locked)
    if (balance.value > 0n) {
      assetBalances.push({
        key: 'binance_cross-margin-balance',
        cryptoId,
        fiatId,
        amount: balance,
        timestamp,
      })
    }
    const borrow = times(
      plus(assetData.borrowed, assetData.interest),
      createBigDecimal(-1n),
    )
    if (borrow.value !== 0n) {
      assetBalances.push({
        key: 'binance_cross-margin-borrow',
        cryptoId,
        fiatId,
        amount: borrow,
        timestamp,
      })
    }
  }

  return assetBalances
}
